import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, CalendarClock, MapPin, Users, ExternalLink, ShieldCheck, XCircle, CheckCircle2 } from "lucide-react";
import portalApi, { formatPortalError } from "./portalApi";
import { previewStore } from "./previewStore";

/**
 * Learner-facing Live Classes section: My upcoming + Open for registration.
 *
 * In admin preview mode we serve static demo data that does not touch the
 * server; register/cancel are no-ops that only update local state.
 */
const PREVIEW_OPEN = [
  {
    id: "preview-live-1", course_code: "CTA-001",
    title: "Live Cohort — Preview Demo Session",
    session_date: "2026-04-15", start_time: "10:00 AM", end_time: "11:30 AM",
    time_zone: "America/Los_Angeles", instructor: "Rev. Dr. Casey Martin", cohost: null,
    seat_limit: 30, seats_used: 12, seats_remaining: 18, registration_deadline: null,
    status: "open", my_registration: null,
  },
];

export default function LearnerLiveClasses({ courseCode = null, title, testId = "live-classes-card" } = {}) {
  const inPreview = previewStore.isActive();
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (inPreview) {
        // Preview state stored in sessionStorage under previewLive
        const raw = sessionStorage.getItem("ccchaps_preview_live");
        const state = raw ? JSON.parse(raw) : { registered: {}, cancelled: {} };
        const isReg = (id) => state.registered[id] && !state.cancelled[id];
        const filtered = courseCode ? PREVIEW_OPEN.filter(s => s.course_code === courseCode) : PREVIEW_OPEN;
        setMine(filtered.filter(s => isReg(s.id)).map(s => ({ registration: { id: `preview-reg-${s.id}`, status: "registered", registered_at: new Date().toISOString() }, session: s })));
        setOpen(filtered.map(s => ({ ...s, my_registration: isReg(s.id) ? { id: `preview-reg-${s.id}`, status: "registered" } : null })));
      } else {
        const params = courseCode ? { course_code: courseCode } : {};
        const [m, o] = await Promise.all([
          portalApi.get("/portal/live-sessions/mine"),
          portalApi.get("/portal/live-sessions", { params }),
        ]);
        const mineFiltered = courseCode ? (m.data || []).filter(r => (r.session?.course_code || "CTA-001") === courseCode) : m.data;
        setMine(mineFiltered); setOpen(o.data);
      }
    } catch (err) { toast.error(formatPortalError(err)); }
    finally { setLoading(false); }
  }, [inPreview, courseCode]);

  useEffect(() => { load(); }, [load]);

  const savePreview = (fn) => {
    const raw = sessionStorage.getItem("ccchaps_preview_live");
    const state = raw ? JSON.parse(raw) : { registered: {}, cancelled: {} };
    fn(state);
    sessionStorage.setItem("ccchaps_preview_live", JSON.stringify(state));
  };

  const register = async (sessionId) => {
    setBusy(sessionId);
    try {
      if (inPreview) { savePreview(s => { s.registered[sessionId] = true; delete s.cancelled[sessionId]; }); toast.success("Registered (preview only)."); await load(); return; }
      await portalApi.post(`/portal/live-sessions/${sessionId}/register`);
      toast.success("You're registered. Zoom info is now on your dashboard.");
      await load();
    } catch (err) { toast.error(formatPortalError(err)); }
    finally { setBusy(null); }
  };

  const cancel = async (sessionId) => {
    if (!window.confirm("Cancel your registration for this live class? Your seat will be released.")) return;
    setBusy(sessionId);
    try {
      if (inPreview) { savePreview(s => { s.cancelled[sessionId] = true; delete s.registered[sessionId]; }); toast.success("Cancelled (preview only)."); await load(); return; }
      await portalApi.post(`/portal/live-sessions/${sessionId}/cancel`);
      toast.success("Registration cancelled.");
      await load();
    } catch (err) { toast.error(formatPortalError(err)); }
    finally { setBusy(null); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = mine.filter(x => x.session.session_date >= today);

  return (
    <div className="mt-8 space-y-6" data-testid={testId}>
      {upcoming.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7">
          <div className="flex items-center gap-2 mb-4">
            <Video className="w-4 h-4 text-[#4A6FA5]"/>
            <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">{title || "My live classes"}</p>
          </div>
          <ul className="space-y-3">
            {upcoming.map(({ registration, session: s }) => {
              const isCompleted = s.session_date < today;
              return (
                <li key={s.id} data-testid={`my-live-${s.id}`} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{s.course_code || "CTA-001"}</p>
                      <p className="font-heading text-lg text-slate-900">{s.title}</p>
                      <p className="mt-1 text-sm text-slate-700 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5 text-slate-400"/>{s.session_date}</span>
                        <span>{s.start_time}–{s.end_time}</span>
                        <span className="inline-flex items-center gap-1 text-slate-500"><MapPin className="w-3.5 h-3.5"/>{s.time_zone}</span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Instructor: {s.instructor}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <ShieldCheck className="w-3 h-3"/> Registered
                    </span>
                  </div>
                  {s.zoom_join_url && (
                    <div className="mt-3 rounded-lg bg-[#F0F4F8] p-3 text-sm">
                      <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#4A6FA5]">Zoom access · for your registration only</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a href={s.zoom_join_url} target="_blank" rel="noopener noreferrer" data-testid={`join-${s.id}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B]">
                          <ExternalLink className="w-3.5 h-3.5"/> JOIN ZOOM CLASS
                        </a>
                        {s.zoom_meeting_id && <span className="text-xs text-slate-600 self-center">Meeting ID: <span className="font-mono">{s.zoom_meeting_id}</span></span>}
                        {s.zoom_passcode && <span className="text-xs text-slate-600 self-center">Passcode: <span className="font-mono">{s.zoom_passcode}</span></span>}
                      </div>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button onClick={() => cancel(s.id)} disabled={busy === s.id} data-testid={`cancel-${s.id}`} className="text-xs text-rose-700 hover:text-rose-900 font-semibold inline-flex items-center gap-1">
                      <XCircle className="w-3.5 h-3.5"/> Cancel registration
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {mine.filter(x => x.session.session_date < today).map(({ session: s }) => (
        <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-4 text-sm flex items-center gap-3">
          <CheckCircle2 className="w-4 h-4 text-slate-400"/>
          <span className="text-slate-500">{s.title} — <span className="uppercase tracking-[0.14em] text-[10px] font-semibold text-slate-500">Class completed</span></span>
        </div>
      ))}

      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7">
        <div className="flex items-center gap-2 mb-4">
          <Video className="w-4 h-4 text-slate-500"/>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{title ? "Upcoming " + title.toLowerCase() : "Upcoming live classes"}</p>
        </div>
        {loading ? <p className="text-sm text-slate-500">Loading…</p> :
          open.length === 0 ? <p className="text-sm text-slate-500" data-testid="live-no-sessions">{courseCode === "HOSP-001" ? "No live session is currently scheduled. Future session dates and access information will be provided to enrolled HOSP-001 learners when available." : "No live sessions currently open for registration. Continue with the self-paced course above."}</p> :
          <ul className="space-y-3">
            {open.map(s => {
              const isRegistered = !!s.my_registration;
              const full = (s.seats_remaining ?? 0) <= 0;
              return (
                <li key={s.id} data-testid={`open-live-${s.id}`} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{s.course_code || "CTA-001"}</p>
                      <p className="font-heading text-lg text-slate-900">{s.title}</p>
                      <p className="mt-1 text-sm text-slate-700 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5 text-slate-400"/>{s.session_date}</span>
                        <span>{s.start_time}–{s.end_time}</span>
                        <span className="inline-flex items-center gap-1 text-slate-500"><MapPin className="w-3.5 h-3.5"/>{s.time_zone}</span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Instructor: {s.instructor}</p>
                      <p className="text-xs text-slate-500"><Users className="w-3 h-3 inline mr-1"/>{s.seats_remaining} of {s.seat_limit} seats available{s.registration_deadline ? ` · Registration closes ${new Date(s.registration_deadline).toLocaleString()}` : ""}</p>
                    </div>
                    {isRegistered ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"><ShieldCheck className="w-3 h-3"/> Registered</span>
                    ) : full ? (
                      <span data-testid={`full-${s.id}`} className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">CLASS FULL</span>
                    ) : (
                      <button onClick={() => register(s.id)} disabled={busy === s.id} data-testid={`register-${s.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B] disabled:opacity-60">
                        {busy === s.id ? "Registering…" : "REGISTER FOR LIVE CLASS"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        }
      </div>
    </div>
  );
}
